import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { permissionsActions } from './slice';

// 🔥 Load Permissions
export function* getPermissions() {
	try {
		// Get the orgID from the selected profile in the state
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Fetch permissions based on the orgID (or other needed params)
		const response = yield call(() => SuperFetch.get('/access'));

		// Check if the response status is OK
		if (response.statusCode === 200) {
			// Dispatch the success action with the permissions data
			yield put(
				permissionsActions.loadPermissionsSuccess({
					orgID,
					data: response.data
				})
			);
		} else {
			// Throw an error if the API response is not successful
			throw new Error(response.error?.message || 'Failed to load permissions');
		}
	} catch (error) {
		yield put(permissionsActions.loadPermissionsError(error.message));
		Notification('error', 'Failed to load permissions.');
	}
}

// 🔥 Grant Permission
export const grantPermissions = function* ({ payload }) {
	try {
		const response = yield call(() => SuperFetch.post('/access/grantPermission', { ...payload }, {}));

		if (response.statusCode === 200) {
			// Check for partial success with warnings
			const warnings = response.data?.warnings;
			if (warnings?.length > 0) {
				Notification('warning', warnings[0]);
			}
			yield put(permissionsActions.grantPermissionSuccess());
			Notification('success', 'Permission granted successfully.');
		} else {
			throw new Error(response.error?.message || response.error || 'Failed to grant permission.');
		}
	} catch (error) {
		yield put(permissionsActions.grantPermissionError(error.message));
		Notification('error', error.message || 'Failed to grant permission.');
	}
};

export function* grantPermissionsSuccess() {
	yield put(permissionsActions.loadPermissions());
}

// 🔥 Revoke Permission
export const revokePermission = function* ({ payload }) {
	try {
		const response = yield call(() => SuperFetch.post('/access/revokePermission', { ...payload }, {}));

		if (response.statusCode === 200) {
			yield put(permissionsActions.revokePermissionSuccess());
			Notification('success', 'Permission revoked successfully.');
		} else {
			throw new Error(response.error?.message || 'Failed to revoke permission');
		}
	} catch (error) {
		yield put(permissionsActions.grantPermissionError(error.message));
		Notification('error', 'Failed to revoke permission.');
	}
};

export function* revokePermissionSuccess() {
	yield put(permissionsActions.loadPermissions());
}

// 🔥 Revoke by ID
export function* deletePermissionByIDs({ payload }) {
	try {
		const { permissionIDs } = payload;

		const response = yield call(() => SuperFetch.post('/access/revokePermissionByIDs', { permissionIDs }, {}));

		if (response.statusCode === 200) {
			yield put(permissionsActions.deletePermissionByIDsSuccess());
			Notification('success', 'Permission removed successfully.');
		} else {
			throw new Error(response.error?.message || 'Failed to remove permission');
		}
	} catch (error) {
		yield put(permissionsActions.deletePermissionByIDsError(error.message));
		Notification('error', 'Failed to remove permission.');
	}
}

// 🔥 Success after delete
export function* deletePermissionSuccess() {
	yield put(permissionsActions.loadPermissions());
}

// 📦 Root Saga
export default function* permissionsSagas() {
	yield takeLatest(permissionsActions.loadPermissions, getPermissions);
	yield takeEvery(permissionsActions.grantPermission, grantPermissions);
	yield takeLatest(permissionsActions.grantPermissionSuccess, grantPermissionsSuccess);
	yield takeEvery(permissionsActions.revokePermission, revokePermission);
	yield takeLatest(permissionsActions.revokePermissionSuccess, revokePermissionSuccess);
	yield takeLatest(permissionsActions.deletePermissionByIDs, deletePermissionByIDs);
	yield takeLatest(permissionsActions.deletePermissionByIDsSuccess, deletePermissionSuccess);
}
